import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { CallingV2 } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";
import {
  LLM_PROVIDERS, LLM_MODELS_BY_PROVIDER,
  STT_PROVIDERS, STT_MODELS_BY_PROVIDER, STT_LANGUAGES,
  TTS_PROVIDERS, TTS_VOICES_BY_PROVIDER,
  REALTIME_PROVIDERS, REALTIME_MODELS_BY_PROVIDER, REALTIME_VOICES_BY_PROVIDER,
  CUSTOM_SENTINEL,
} from "./modelOptions";

/**
 * ProviderScopedSelect — condition-based dropdown:
 *   • Selected provider drives which model/voice options appear.
 *   • Picking "Custom…" reveals a paired text input so admins can drop in any
 *     model string the runtime supports.
 *   • Loading an existing template with a value not in the current provider's
 *     preset list automatically shows it in custom mode.
 */
function ProviderScopedSelect({ provider, options, value, onChange, placeholder = "Pick or type custom" }) {
  const presets = (options && options[provider]) || [];
  const valueIsPreset = !!value && presets.includes(value);
  // Local "user clicked Custom" state. Goes true on explicit click OR when
  // the loaded value is non-empty and not in the preset list.
  const [customMode, setCustomMode] = React.useState(false);
  const isCustom = customMode || (!!value && !valueIsPreset);

  // When the provider changes, drop out of custom mode so the user sees the
  // new provider's preset list. The form value stays as-is — admins can
  // pick again or re-enter custom.
  React.useEffect(() => { setCustomMode(false); }, [provider]);

  return (
    <>
      <select
        className="form-select"
        value={isCustom ? CUSTOM_SENTINEL : (valueIsPreset ? value : "")}
        onChange={(e) => {
          const v = e.target.value;
          if (v === CUSTOM_SENTINEL) {
            setCustomMode(true);
            // Don't clobber an existing custom value — let the input edit it.
          } else {
            setCustomMode(false);
            onChange(v);
          }
        }}
      >
        <option value="">— choose —</option>
        {presets.map((p) => <option key={p} value={p}>{p}</option>)}
        <option value={CUSTOM_SENTINEL}>Custom…</option>
      </select>
      {isCustom && (
        <input
          className="form-control mt-2"
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => { setCustomMode(true); onChange(e.target.value); }}
          autoFocus
        />
      )}
    </>
  );
}

/**
 * Calling V2 → Agent Template editor (single-page scroll, no multi-step).
 *
 * Sections: Basics → Brain (LLM/STT/TTS/Realtime) → Persona → Pricing & Marketplace.
 * Knowledge / Tools / Handoffs sections are added in later phases.
 */
export default function CallingV2TemplateEditor() {
  document.title = "Digital Employees — Edit V2 Template";
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [otherTemplates, setOtherTemplates] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("pipeline");
  const [handoffSearch, setHandoffSearch] = useState("");
  const [catalog, setCatalog] = useState(null);
  const previewAudioRef = React.useRef(null);
  const [previewingKey, setPreviewingKey] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      slug: "",
      persona_name: "",
      category: "general",
      direction: "outbound",
      mode: "pipeline",
      description: "",
      // Brain
      llm_provider: "openai",
      llm_model: "gpt-4o",
      llm_temperature: 0.7,
      stt_provider: "deepgram",
      stt_model: "nova-2",
      stt_language: "en",
      tts_provider: "openai",
      tts_voice: "alloy",
      realtime_provider: "openai",
      realtime_model: "gpt-realtime",
      realtime_voice: "marin",
      // Persona
      greeting: "",
      prompt: "",
      // Behavior
      human_transfer_number: "",
      human_transfer_email: "",
      transfer_instruction: "",
      allowed_handoffs: [],
      default_tools: [],
      max_call_minutes: 30,  // form-only unit; converted to seconds on submit + on load from server.
      max_handoffs: 10,
      max_tool_calls: 50,
      // Pricing
      monthly_price: 0,
      included_minutes: 0,
      unit_label: "minutes",
      is_marketplace: true,
      is_active: true,
      marketplace_description: "",
      disclaimer: "",
      features: [{ text: "" }],
      profile_picture: "",
      auto_end_call: true,
      noise_cancellation_enabled: false,
      voice_min_endpointing_delay: 0.5,
      voice_max_endpointing_delay: 6.0,
      voice_vad_min_silence_duration: 0.4,
      voice_vad_min_speech_duration: 0.1,
      voice_vad_activation_threshold: 0.45,
      voicemail_detection: false,
      voicemail_action: "hangup",
      voicemail_message: "",
    },
  });

  const watchedMode = watch("mode");
  useEffect(() => { setMode(watchedMode); }, [watchedMode]);

  const { fields: featureFields, append: appendFeature, remove: removeFeature } = useFieldArray({
    control, name: "features",
  });

  // Load referenced catalogs once
  useEffect(() => {
    CallingV2.listTemplates({ active: "true" })
      .then((r) => setOtherTemplates((r?.data?.results || []).filter((t) => String(t.id) !== String(id))))
      .catch(() => setOtherTemplates([]));
    CallingV2.listTools({ active: "true" })
      .then((r) => setTools(r?.data?.results || []))
      .catch(() => setTools([]));
    CallingV2.listProviders()
      .then((r) => setCatalog(r?.data || null))
      .catch(() => setCatalog(null));
  }, [id]);

  // Cleanup any voice preview when unmounting
  useEffect(() => () => {
    if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
  }, []);

  const playVoice = (key, url) => {
    if (!url) return;
    if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
    if (previewingKey === key) { setPreviewingKey(""); return; }
    const a = new Audio(url);
    previewAudioRef.current = a;
    a.onended = () => { setPreviewingKey(""); previewAudioRef.current = null; };
    a.onerror = () => { setPreviewingKey(""); previewAudioRef.current = null; };
    a.play().then(() => setPreviewingKey(key)).catch(() => setPreviewingKey(""));
  };

  // Catalog helpers — fall back to modelOptions constants while the API loads.
  const providersList = (kind, fallback) => {
    const keys = catalog && catalog[kind] ? Object.keys(catalog[kind]) : [];
    return keys.length ? keys : fallback;
  };
  const modelsFor = (kind, prov, fallbackMap) => {
    const block = catalog && catalog[kind] ? catalog[kind][prov] : null;
    if (block?.models?.length) return block.models.map((m) => ({ id: m.id, label: m.label || m.id }));
    return (fallbackMap[prov] || []).map((m) => ({ id: m, label: m }));
  };
  const voicesForCat = (kind, prov) => {
    const block = catalog && catalog[kind] ? catalog[kind][prov] : null;
    return block?.voices || [];
  };
  const ttsFreeText = (prov) => (catalog?.tts?.[prov]?.voice_mode === "voice_id_free_text");
  const ttsHelp = (prov) => catalog?.tts?.[prov]?.voice_help;
  const langsFor = (prov, fallback) => {
    const block = catalog?.stt?.[prov];
    if (block?.languages?.length) return block.languages.map((l) => ({ id: l.id, label: l.label || l.id }));
    return fallback.map((l) => ({ id: l, label: l }));
  };

  // Load template if editing
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    CallingV2.getTemplate(id)
      .then((r) => {
        const t = r?.data || {};
        reset({
          name: t.name || "",
          slug: t.slug || "",
          persona_name: t.persona_name || "",
          category: t.category || "general",
          direction: t.direction || "outbound",
          mode: t.mode || "pipeline",
          description: t.description || "",
          llm_provider: t.llm?.provider || "openai",
          llm_model: t.llm?.model || "gpt-4o",
          llm_temperature: t.llm?.temperature ?? 0.7,
          stt_provider: t.stt?.provider || "deepgram",
          stt_model: t.stt?.model || "nova-2",
          stt_language: t.stt?.language || "en",
          tts_provider: t.tts?.provider || "openai",
          tts_voice: t.tts?.voice || "alloy",
          realtime_provider: t.realtime?.provider || "openai",
          realtime_model: t.realtime?.model || "gpt-realtime",
          realtime_voice: t.realtime?.voice || "marin",
          greeting: t.greeting || "",
          prompt: t.prompt || "",
          human_transfer_number: t.human_transfer_number || "",
          human_transfer_email: t.human_transfer_email || "",
          transfer_instruction: t.transfer_instruction || "",
          allowed_handoffs: t.allowed_handoffs || [],
          default_tools: t.default_tools || [],
          // Stored on the server as seconds; edited here in minutes so admins
          // don't have to reach for a calculator when picking "30 min".
          max_call_minutes: Math.round((t.limits?.max_call_seconds ?? 1800) / 60),
          max_handoffs: t.limits?.max_handoffs ?? 10,
          max_tool_calls: t.limits?.max_tool_calls ?? 50,
          monthly_price: t.monthly_price ?? 0,
          included_minutes: t.included_minutes ?? 0,
          unit_label: "minutes",
          is_marketplace: !!t.is_marketplace,
          is_active: !!t.is_active,
          marketplace_description: t.marketplace_description || "",
          disclaimer: t.disclaimer || "",
          features: (t.features && t.features.length ? t.features : [""]).map((f) => ({ text: typeof f === "string" ? f : (f?.text || "") })),
          profile_picture: t.profile_picture || "",
          auto_end_call: t.auto_end_call !== false,
          noise_cancellation_enabled: t.noise_cancellation_enabled === true,
          voice_min_endpointing_delay: t.voice_settings?.min_endpointing_delay ?? 0.5,
          voice_max_endpointing_delay: t.voice_settings?.max_endpointing_delay ?? 6.0,
          voice_vad_min_silence_duration: t.voice_settings?.vad_min_silence_duration ?? 0.4,
          voice_vad_min_speech_duration: t.voice_settings?.vad_min_speech_duration ?? 0.1,
          voice_vad_activation_threshold: t.voice_settings?.vad_activation_threshold ?? 0.45,
          voicemail_detection: t.voicemail_settings?.detection === true,
          voicemail_action: t.voicemail_settings?.action || "hangup",
          voicemail_message: t.voicemail_settings?.message || "",
        });
      })
      .catch(() => triggerAlert("error", "Oops...", "Could not load template."))
      .finally(() => setLoading(false));
  }, [id, isEdit, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    const slug = (data.slug || data.name || "").trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
    const features = (data.features || []).map((f) => (f?.text || "").trim()).filter(Boolean);
    const payload = {
      name: data.name.trim(),
      slug,
      persona_name: (data.persona_name || "").trim(),
      category: data.category || "general",
      direction: data.direction || "outbound",
      mode: data.mode || "pipeline",
      description: data.description || "",
      llm: { provider: data.llm_provider, model: data.llm_model, temperature: parseFloat(data.llm_temperature) || 0.7 },
      stt: { provider: data.stt_provider, model: data.stt_model, language: data.stt_language || "en" },
      tts: { provider: data.tts_provider, voice: data.tts_voice },
      realtime: { provider: data.realtime_provider, model: data.realtime_model, voice: data.realtime_voice },
      greeting: data.greeting || "",
      prompt: data.prompt || "",
      human_transfer_number: data.human_transfer_number || "",
      human_transfer_email: (data.human_transfer_email || "").trim(),
      transfer_instruction: data.transfer_instruction || "",
      allowed_handoffs: (data.allowed_handoffs || []).map((v) => Number(v)).filter(Boolean),
      default_tools: (data.default_tools || []).map((v) => Number(v)).filter(Boolean),
      limits: {
        // Convert minutes → seconds on the way out; the vendor expects seconds.
        max_call_seconds: (parseInt(data.max_call_minutes, 10) || 30) * 60,
        max_handoffs: parseInt(data.max_handoffs, 10) || 10,
        max_tool_calls: parseInt(data.max_tool_calls, 10) || 50,
      },
      monthly_price: parseFloat(data.monthly_price) || 0,
      included_minutes: parseFloat(data.included_minutes) || 0,
      unit_label: "minutes",
      is_marketplace: !!data.is_marketplace,
      is_active: !!data.is_active,
      marketplace_description: data.marketplace_description || "",
      features,
      disclaimer: data.disclaimer || "",
      profile_picture: data.profile_picture || "",
      auto_end_call: !!data.auto_end_call,
      noise_cancellation_enabled: !!data.noise_cancellation_enabled,
      voice_settings: {
        min_endpointing_delay: parseFloat(data.voice_min_endpointing_delay) || 0.5,
        max_endpointing_delay: parseFloat(data.voice_max_endpointing_delay) || 6.0,
        vad_min_silence_duration: parseFloat(data.voice_vad_min_silence_duration) || 0.4,
        vad_min_speech_duration: parseFloat(data.voice_vad_min_speech_duration) || 0.1,
        vad_activation_threshold: parseFloat(data.voice_vad_activation_threshold) || 0.45,
      },
      voicemail_settings: {
        detection: !!data.voicemail_detection,
        action: data.voicemail_action || "hangup",
        message: (data.voicemail_message || "").trim(),
      },
    };

    try {
      if (isEdit) {
        await CallingV2.updateTemplate(id, payload);
        triggerAlert("success", "Saved", "Template updated.");
      } else {
        const r = await CallingV2.createTemplate(payload);
        triggerAlert("success", "Created", "Template published.");
        navigate(`/v2/templates/${r?.data?.id}`);
        return;
      }
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.response?.data && typeof e.response.data === "object" ? Object.values(e.response.data).flat().join(" ") : null)
        || "Save failed.";
      triggerAlert("error", "Oops...", msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading={isEdit ? "Edit Template" : "New Template"} heading={isEdit ? "Calling V2 / Templates / Edit" : "Calling V2 / Templates / New"} />
            {loading ? (
              <div className="loader-overlay text-white"><Loader /></div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* ── Basics ── */}
                <div className="card mb-3"><div className="card-body">
                  <h5 className="mb-3">Basics</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Name <span className="text-danger">*</span>
                        <small className="text-muted ms-2">Marketplace label customers see on the card</small>
                      </label>
                      <input className="form-control" placeholder="e.g. Sales Agent" {...register("name", { required: "Name is required" })} />
                      {errors.name && <div className="text-danger font_14 mt-1">{errors.name.message}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Persona name
                        <small className="text-muted ms-2">Human first name the agent uses on calls — defaults to Name when blank</small>
                      </label>
                      <input className="form-control" placeholder="e.g. Sarah" {...register("persona_name")} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Direction</label>
                      <select className="form-select" {...register("direction")}>
                        <option value="outbound">Outbound</option>
                        <option value="inbound">Inbound</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Mode</label>
                      <select className="form-select" {...register("mode")}>
                        <option value="pipeline">Pipeline (STT → LLM → TTS)</option>
                        <option value="realtime">Realtime (Speech-to-Speech)</option>
                      </select>
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Description</label>
                      <textarea className="form-control" rows="2" {...register("description")} />
                    </div>
                  </div>
                </div></div>

                {/* ── Brain ── */}
                <div className="card mb-3"><div className="card-body">
                  <h5 className="mb-3">Brain</h5>
                  <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Pick a provider/model from the dropdown — or type a custom value if you're using something
                    not in the list. The runtime forwards the string verbatim, so any provider/model name
                    your downstream account supports will work.
                  </p>
                  {(() => {
                    const llmProvs = providersList("llm", LLM_PROVIDERS);
                    const sttProvs = providersList("stt", STT_PROVIDERS);
                    const ttsProvs = providersList("tts", TTS_PROVIDERS);
                    const rtProvs = providersList("realtime", REALTIME_PROVIDERS);
                    const llmMods = (catalog && Object.keys(catalog?.llm || {}).reduce((acc, p) => { acc[p] = modelsFor("llm", p, LLM_MODELS_BY_PROVIDER).map(m => m.id); return acc; }, {})) || LLM_MODELS_BY_PROVIDER;
                    const sttMods = (catalog && Object.keys(catalog?.stt || {}).reduce((acc, p) => { acc[p] = modelsFor("stt", p, STT_MODELS_BY_PROVIDER).map(m => m.id); return acc; }, {})) || STT_MODELS_BY_PROVIDER;
                    const rtMods = (catalog && Object.keys(catalog?.realtime || {}).reduce((acc, p) => { acc[p] = modelsFor("realtime", p, REALTIME_MODELS_BY_PROVIDER).map(m => m.id); return acc; }, {})) || REALTIME_MODELS_BY_PROVIDER;
                    const VoiceRow = ({ kind, label, prov, valueField }) => {
                      const voices = voicesForCat(kind, prov);
                      const value = watch(valueField);
                      const sel = voices.find((v) => v.id === value);
                      const previewUrl = sel?.preview_url || "";
                      const previewKey = `${kind}:${value}`;
                      const isPlaying = previewingKey === previewKey;
                      // Local UI state for the gender filter chip — only one
                      // active filter per VoiceRow instance. Default 'all' so
                      // first paint matches the existing behavior.
                      const [genderFilter, setGenderFilter] = React.useState("all");
                      // Filter renders only when the provider's catalog has
                      // at least one male or female voice. A pure-neutral
                      // catalog has nothing useful to filter on.
                      const hasGenderHints = voices.some(
                        (v) => v.gender === "female" || v.gender === "male"
                      );
                      if (kind === "tts" && ttsFreeText(prov)) {
                        return (
                          <div className="col-md-6 mb-3">
                            <label className="form-label">{label}</label>
                            <div className="input-group">
                              <input className="form-control" placeholder="Paste a voice id"
                                value={value || ""} onChange={(e) => setValue(valueField, e.target.value, { shouldDirty: true })} />
                              <button type="button" className="btn btn-outline-secondary" disabled={!previewUrl}
                                onClick={() => playVoice(previewKey, previewUrl)}
                                title={previewUrl ? (isPlaying ? "Stop preview" : "Play preview") : "No preview"}>
                                {isPlaying ? "■" : "▶"}
                              </button>
                            </div>
                            {ttsHelp(prov) && <small className="text-muted">{ttsHelp(prov)}</small>}
                          </div>
                        );
                      }
                      // Catalog voices come straight from the runtime + carry
                      // ``gender``. Fallback constants don't, so the filter
                      // collapses to a no-op when we're in the fallback path.
                      const catalogVoices = voices.length
                        ? voices
                        : (kind === "tts"
                            ? (TTS_VOICES_BY_PROVIDER[prov] || []).map((v) => ({ id: v, label: v }))
                            : (REALTIME_VOICES_BY_PROVIDER[prov] || []).map((v) => ({ id: v, label: v })));
                      // Apply the gender filter (only when the vendor classified
                      // at least one voice).
                      // Vendor-tagged 'neutral' / unclassified voices only
                      // show under 'all' — the filter dropdown is restricted
                      // to male/female.
                      const filtered = (hasGenderHints && genderFilter !== "all")
                        ? catalogVoices.filter((v) => v.gender === genderFilter)
                        : catalogVoices;
                      const renderOpt = (v) => {
                        // Tiny gender suffix in the option text — chips/badges
                        // aren't well-supported inside native <option> elements
                        // on most browsers, so we inline the marker.
                        const g = v.gender;
                        const tag = g === "female" ? " · F"
                          : g === "male" ? " · M"
                          : g === "neutral" ? " · N"
                          : "";
                        return `${v.label || v.id}${tag}`;
                      };
                      // Auto-promote: when the gender filter changes and the
                      // current form value doesn't match the new filter, pick
                      // the first matching voice. Otherwise the <select>
                      // visually goes blank (no <option> with value=current)
                      // and the user has to expand + re-select. The voice
                      // dropdown stays in a valid state regardless of toggles.
                      const handleGenderChange = (next) => {
                        setGenderFilter(next);
                        const pool = next === "all"
                          ? catalogVoices
                          : catalogVoices.filter((v) => v.gender === next);
                        if (pool.length === 0) return;
                        if (value && pool.some((v) => v.id === value)) return;
                        setValue(valueField, pool[0].id, { shouldDirty: true });
                      };
                      return (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">{label}</label>
                          <div className="input-group">
                            {hasGenderHints && (
                              <select
                                className="form-select flex-grow-0"
                                style={{ maxWidth: 110 }}
                                value={genderFilter}
                                onChange={(e) => handleGenderChange(e.target.value)}
                                aria-label="Gender filter">
                                <option value="all">All</option>
                                <option value="female">Female</option>
                                <option value="male">Male</option>
                              </select>
                            )}
                            <select className="form-select" value={value || ""}
                              onChange={(e) => setValue(valueField, e.target.value, { shouldDirty: true })}>
                              {filtered.length === 0 && (
                                <option value="" disabled>
                                  No {genderFilter !== "all" ? `${genderFilter} ` : ""}voices for this provider
                                </option>
                              )}
                              {filtered.map((v) => <option key={v.id} value={v.id}>{renderOpt(v)}</option>)}
                            </select>
                            <button type="button" className="btn btn-outline-secondary" disabled={!previewUrl}
                              onClick={() => playVoice(previewKey, previewUrl)}
                              title={previewUrl ? (isPlaying ? "Stop preview" : "Play preview") : "No preview"}>
                              {isPlaying ? "■" : "▶"}
                            </button>
                          </div>
                        </div>
                      );
                    };
                    return mode === "pipeline" ? (
                    <>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">LLM Provider</label>
                          <select className="form-select" {...register("llm_provider")}>
                            {llmProvs.map((p) => <option key={p} value={p}>{catalog?.llm?.[p]?.label || p}</option>)}
                          </select>
                        </div>
                        <div className="col-md-5 mb-3">
                          <label className="form-label">LLM Model</label>
                          <ProviderScopedSelect
                            provider={watch("llm_provider")}
                            options={llmMods}
                            value={watch("llm_model")}
                            onChange={(v) => setValue("llm_model", v, { shouldDirty: true })}
                            placeholder="Custom model id"
                          />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Temperature</label>
                          <input type="number" step="0.1" min="0" max="2" className="form-control" {...register("llm_temperature")} />
                          <small className="text-muted">0 = deterministic, 1 = balanced, 2 = creative</small>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">STT Provider</label>
                          <select className="form-select" {...register("stt_provider")}>
                            {sttProvs.map((p) => <option key={p} value={p}>{catalog?.stt?.[p]?.label || p}</option>)}
                          </select>
                        </div>
                        <div className="col-md-5 mb-3">
                          <label className="form-label">STT Model</label>
                          <ProviderScopedSelect
                            provider={watch("stt_provider")}
                            options={sttMods}
                            value={watch("stt_model")}
                            onChange={(v) => setValue("stt_model", v, { shouldDirty: true })}
                            placeholder="Custom STT model name"
                          />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Language</label>
                          <select className="form-select" {...register("stt_language")}>
                            {langsFor(watch("stt_provider"), STT_LANGUAGES).map((l) => (
                              <option key={l.id} value={l.id}>{l.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">TTS Provider</label>
                          <select className="form-select" {...register("tts_provider")}>
                            {ttsProvs.map((p) => <option key={p} value={p}>{catalog?.tts?.[p]?.label || p}</option>)}
                          </select>
                        </div>
                        <VoiceRow kind="tts" label="TTS Voice" prov={watch("tts_provider")} valueField="tts_voice" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Realtime Provider</label>
                          <select className="form-select" {...register("realtime_provider")}>
                            {rtProvs.map((p) => <option key={p} value={p}>{catalog?.realtime?.[p]?.label || p}</option>)}
                          </select>
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Realtime Model</label>
                          <ProviderScopedSelect
                            provider={watch("realtime_provider")}
                            options={rtMods}
                            value={watch("realtime_model")}
                            onChange={(v) => setValue("realtime_model", v, { shouldDirty: true })}
                            placeholder="Custom realtime model id"
                          />
                        </div>
                        <VoiceRow kind="realtime" label="Realtime Voice" prov={watch("realtime_provider")} valueField="realtime_voice" />
                      </div>
                      <div className="alert alert-info py-2 mb-0">
                        <strong>Realtime mode</strong> bypasses the pipeline STT/LLM/TTS chain. The runtime
                        uses a single speech-to-speech model end-to-end — lower latency, less control over
                        individual stages.
                      </div>
                    </>
                  );
                  })()}
                </div></div>

                {/* ── Call behaviour & voice tuning ── */}
                <div className="card mb-3"><div className="card-body">
                  <h5 className="mb-3">Call behaviour & voice tuning</h5>
                  <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Endpointing controls how quickly the runtime decides the caller has finished speaking.
                    VAD thresholds determine when voice activity is detected. Defaults match the vendor's recommended settings.
                  </p>
                  <div className="form-check mb-3">
                    <input className="form-check-input" type="checkbox" id="auto_end_call" {...register("auto_end_call")} />
                    <label htmlFor="auto_end_call" className="form-check-label">
                      Auto end call &nbsp;
                      <small className="text-muted">— let the agent hang up when its task is complete</small>
                    </label>
                  </div>
                  <div className="form-check mb-3">
                    <input className="form-check-input" type="checkbox" id="noise_cancellation_enabled" {...register("noise_cancellation_enabled")} />
                    <label htmlFor="noise_cancellation_enabled" className="form-check-label">
                      Noise cancellation &nbsp;
                      <small className="text-muted">— suppress background noise on the caller side. Default off; customer can override on their agent.</small>
                    </label>
                  </div>
                  <div className="row g-4">
                    <div className="col-md-6 col-lg-4 mb-3">
                      <label className="form-label">Min endpointing delay (s)</label>
                      <input type="number" step="0.05" min="0" className="form-control" {...register("voice_min_endpointing_delay")} />
                    </div>
                    <div className="col-md-6 col-lg-4 mb-3">
                      <label className="form-label">Max endpointing delay (s)</label>
                      <input type="number" step="0.1" min="0" className="form-control" {...register("voice_max_endpointing_delay")} />
                    </div>
                    <div className="col-md-6 col-lg-4 mb-3">
                      <label className="form-label">VAD min silence (s)</label>
                      <input type="number" step="0.05" min="0" className="form-control" {...register("voice_vad_min_silence_duration")} />
                    </div>
                    <div className="col-md-6 col-lg-4 mb-3">
                      <label className="form-label">VAD min speech (s)</label>
                      <input type="number" step="0.05" min="0" className="form-control" {...register("voice_vad_min_speech_duration")} />
                    </div>
                    <div className="col-md-6 col-lg-4 mb-3">
                      <label className="form-label">VAD activation threshold</label>
                      <input type="number" step="0.05" min="0" max="1" className="form-control" {...register("voice_vad_activation_threshold")} />
                    </div>
                  </div>
                </div></div>

                {/* ── Voicemail (outbound only) ── */}
                {watch("direction") !== "inbound" && (
                  <div className="card mb-3"><div className="card-body">
                    <h5 className="mb-3">Voicemail handling</h5>
                    <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                      Outbound only. Runs answering-machine detection on pickup and either hangs up
                      or leaves a recorded message after the beep. Inbound calls ignore these settings.
                    </p>
                    <div className="form-check mb-3">
                      <input className="form-check-input" type="checkbox" id="voicemail_detection" {...register("voicemail_detection")} />
                      <label htmlFor="voicemail_detection" className="form-check-label">
                        Detect answering machines &nbsp;
                        <small className="text-muted">— turn off if you only want live answers</small>
                      </label>
                    </div>
                    {watch("voicemail_detection") && (
                      <div className="row g-3">
                        <div className="col-md-6 mb-2">
                          <label className="form-label">When a machine answers</label>
                          <select className="form-select" {...register("voicemail_action")}>
                            <option value="hangup">Hang up immediately</option>
                            <option value="leave_message">Wait for the beep, then leave a message</option>
                          </select>
                        </div>
                        {watch("voicemail_action") === "leave_message" && (
                          <div className="col-12 mb-2">
                            <label className="form-label">Voicemail message</label>
                            <textarea className="form-control" rows="2"
                              placeholder="Leave blank to use the agent's greeting"
                              {...register("voicemail_message")} />
                            <small className="text-muted">
                              What the agent says to the voicemail box. Blank → vendor falls back to the agent's greeting.
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                  </div></div>
                )}

                {/* ── Persona ── */}
                <div className="card mb-3"><div className="card-body">
                  <h5 className="mb-3">Persona</h5>
                  <div className="row">
                    <div className="col-12 mb-3">
                      <label className="form-label">Greeting</label>
                      <input className="form-control" placeholder="e.g. Hi, this is Sarah from {business_name}, how can I help?" {...register("greeting")} />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">System Prompt <span className="text-danger">*</span></label>
                      <textarea className="form-control" rows="8" style={{ fontFamily: "ui-monospace, monospace", fontSize: 13 }} {...register("prompt", { required: "Prompt is required" })} />
                      {errors.prompt && <div className="text-danger font_14 mt-1">{errors.prompt.message}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Human Transfer Number</label>
                      <input className="form-control" placeholder="+14155551234"
                        {...register("human_transfer_number", {
                          validate: (v) => {
                            const raw = (v || '').trim()
                            if (!raw) return true
                            const compact = raw.replace(/[\s()\-]/g, '')
                            return /^\+[1-9]\d{6,14}$/.test(compact) ||
                              "Must be E.164 — start with '+' and the country code (e.g. +14155551234)."
                          },
                        })} />
                      {errors.human_transfer_number && (
                        <div className="text-danger font_14 mt-1">{errors.human_transfer_number.message}</div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Notify Email (optional)</label>
                      <input type="email" className="form-control" placeholder="livedesk@yourco.com" {...register("human_transfer_email")} />
                      <div className="form-text small">When set, the notify_handoff LLM tool is auto-attached so the AI emails this address with a short summary before transferring.</div>
                    </div>
                    <div className="col-md-6 mb-3"><label className="form-label">Transfer Instruction</label><input className="form-control" placeholder='e.g. "If the caller asks for a human, transfer them."' {...register("transfer_instruction")} /></div>
                    <div className="col-md-4 mb-3"><label className="form-label">Max call minutes</label><input type="number" min="1" className="form-control" {...register("max_call_minutes")} /><div className="form-text small">Stored on the vendor as seconds; edited here in minutes for readability.</div></div>
                    <div className="col-md-4 mb-3"><label className="form-label">Max handoffs</label><input type="number" className="form-control" {...register("max_handoffs")} /></div>
                    <div className="col-md-4 mb-3"><label className="form-label">Max tool calls</label><input type="number" className="form-control" {...register("max_tool_calls")} /></div>
                  </div>
                </div></div>

                {/* ── Default Tools ── */}
                <div className="card mb-3"><div className="card-body">
                  <h5 className="mb-3">Default Tools</h5>
                  <p className="text-muted mb-2">
                    HTTP tools cloned customers inherit on their copy of this agent. Pick any
                    from the catalog; customers can add/remove tools on their own copy later.
                  </p>
                  {tools.length === 0 ? (
                    <div className="text-muted small">No tools in the catalog. Visit <a href="/v2/tools">Tool Catalog</a> to add some.</div>
                  ) : (
                    <select className="form-select" multiple size="6" {...register("default_tools")}>
                      {tools.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — {t.method} {t.endpoint}
                        </option>
                      ))}
                    </select>
                  )}
                  <small className="text-muted">Cmd/Ctrl+click to toggle multiple selections.</small>
                </div></div>

                {/* ── Behavior — Allowed Handoffs ── */}
                <div className="card mb-3"><div className="card-body">
                  <h5 className="mb-3">Allowed Handoffs</h5>
                  <p className="text-muted mb-2">
                    Other templates this agent can transfer to during a call. Customers cloning
                    this template inherit the same handoff network (as new copies of those targets).
                  </p>
                  {otherTemplates.length === 0 ? (
                    <div className="text-muted small">No other templates yet — create more to enable multi-agent handoffs.</div>
                  ) : (
                    <Controller
                      control={control}
                      name="allowed_handoffs"
                      render={({ field }) => {
                        const selected = (field.value || []).map(String);
                        const isSelected = (id) => selected.includes(String(id));
                        const toggle = (id) => {
                          const s = String(id);
                          field.onChange(isSelected(id) ? selected.filter((x) => x !== s) : [...selected, s]);
                        };
                        const remove = (id) => field.onChange(selected.filter((x) => x !== String(id)));
                        const clear = () => field.onChange([]);
                        const selectAll = () =>
                          field.onChange(otherTemplates.map((t) => String(t.id)));
                        const q = (handoffSearch || "").toLowerCase();
                        const filtered = q
                          ? otherTemplates.filter((tpl) =>
                              (tpl.name || "").toLowerCase().includes(q) ||
                              (tpl.direction || "").toLowerCase().includes(q) ||
                              (tpl.mode || "").toLowerCase().includes(q)
                            )
                          : otherTemplates;

                        const selectedTemplates = otherTemplates.filter((t) => isSelected(t.id));

                        const initials = (name) => {
                          const parts = String(name || "?").trim().split(/\s+/).slice(0, 2);
                          return parts.map((p) => p[0]).join("").toUpperCase() || "?";
                        };
                        const colorFromId = (id) => {
                          const palette = [
                            ["#fee2e2", "#b91c1c"], ["#ffedd5", "#c2410c"], ["#fef3c7", "#a16207"],
                            ["#dcfce7", "#15803d"], ["#cffafe", "#0e7490"], ["#dbeafe", "#1d4ed8"],
                            ["#e0e7ff", "#3730a3"], ["#f3e8ff", "#7c3aed"], ["#fce7f3", "#be185d"],
                          ];
                          return palette[Number(id) % palette.length];
                        };

                        return (
                          <div>
                            {/* Header — search + counters + bulk actions */}
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                              <div className="position-relative flex-grow-1" style={{ maxWidth: 420 }}>
                                <i className="bi bi-search position-absolute"
                                  style={{ left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 14 }} />
                                <input
                                  type="search"
                                  className="form-control"
                                  placeholder="Search by name, direction, or mode..."
                                  value={handoffSearch}
                                  onChange={(e) => setHandoffSearch(e.target.value)}
                                  style={{ paddingLeft: 36, height: 38 }}
                                />
                              </div>
                              <span
                                className="badge"
                                style={{
                                  background: selected.length ? "#eef2ff" : "#f1f5f9",
                                  color: selected.length ? "#3730a3" : "#475569",
                                  padding: "8px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                                }}
                              >
                                {selected.length} of {otherTemplates.length} selected
                              </span>
                              <div className="ms-auto d-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={selectAll}
                                  disabled={selected.length === otherTemplates.length}
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={clear}
                                  disabled={selected.length === 0}
                                >
                                  Clear
                                </button>
                              </div>
                            </div>

                            {/* Selected pills strip */}
                            {selectedTemplates.length > 0 && (
                              <div
                                className="d-flex flex-wrap gap-2 mb-3 p-2"
                                style={{
                                  background: "#f8fafc",
                                  border: "1px dashed #cbd5e1",
                                  borderRadius: 10,
                                }}
                              >
                                <span className="text-muted small me-1" style={{ alignSelf: "center" }}>
                                  Active handoffs:
                                </span>
                                {selectedTemplates.map((tpl) => {
                                  const [bg, fg] = colorFromId(tpl.id);
                                  return (
                                    <span
                                      key={tpl.id}
                                      className="d-inline-flex align-items-center gap-2"
                                      style={{
                                        background: bg, color: fg,
                                        padding: "4px 10px 4px 4px",
                                        borderRadius: 999, fontSize: 12, fontWeight: 600,
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: 22, height: 22, borderRadius: "50%",
                                          background: fg, color: "#fff",
                                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                                          fontSize: 10, fontWeight: 700,
                                        }}
                                      >
                                        {initials(tpl.name)}
                                      </span>
                                      {tpl.name}
                                      <button
                                        type="button"
                                        onClick={() => remove(tpl.id)}
                                        title="Remove"
                                        style={{
                                          border: 0, background: "transparent", color: fg,
                                          fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2, cursor: "pointer",
                                        }}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            )}

                            {/* Card grid */}
                            {filtered.length === 0 ? (
                              <div
                                className="text-center text-muted py-4"
                                style={{ border: "1px dashed #cbd5e1", borderRadius: 10 }}
                              >
                                No templates match "{handoffSearch}".
                              </div>
                            ) : (
                              <div
                                className="d-grid gap-2"
                                style={{
                                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                                  maxHeight: 360, overflowY: "auto",
                                  padding: 2,
                                }}
                              >
                                {filtered.map((tpl) => {
                                  const on = isSelected(tpl.id);
                                  const [bg, fg] = colorFromId(tpl.id);
                                  return (
                                    <button
                                      type="button"
                                      key={tpl.id}
                                      onClick={() => toggle(tpl.id)}
                                      style={{
                                        textAlign: "left",
                                        position: "relative",
                                        background: on ? "#f0f5ff" : "#fff",
                                        border: on ? "2px solid #4f46e5" : "1px solid #e5e7eb",
                                        borderRadius: 10,
                                        padding: 12,
                                        cursor: "pointer",
                                        transition: "all 120ms",
                                        boxShadow: on ? "0 1px 3px rgba(79, 70, 229, 0.18)" : "none",
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!on) e.currentTarget.style.borderColor = "#94a3b8";
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!on) e.currentTarget.style.borderColor = "#e5e7eb";
                                      }}
                                    >
                                      {on && (
                                        <span
                                          style={{
                                            position: "absolute", top: 8, right: 8,
                                            width: 20, height: 20, borderRadius: "50%",
                                            background: "#4f46e5", color: "#fff",
                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 12, fontWeight: 700,
                                          }}
                                        >
                                          ✓
                                        </span>
                                      )}
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <span
                                          style={{
                                            width: 36, height: 36, borderRadius: 8,
                                            background: bg, color: fg,
                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 13, fontWeight: 700, flexShrink: 0,
                                          }}
                                        >
                                          {initials(tpl.name)}
                                        </span>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div
                                            style={{
                                              fontWeight: 600, fontSize: 14, color: "#0f172a",
                                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                              paddingRight: on ? 24 : 0,
                                            }}
                                          >
                                            {tpl.name}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="d-flex gap-1">
                                        <span
                                          className="badge"
                                          style={{
                                            background: "#f1f5f9", color: "#475569",
                                            fontSize: 10, fontWeight: 600, padding: "3px 8px",
                                          }}
                                        >
                                          {(tpl.direction || "—").toUpperCase()}
                                        </span>
                                        <span
                                          className="badge"
                                          style={{
                                            background: "#f1f5f9", color: "#475569",
                                            fontSize: 10, fontWeight: 600, padding: "3px 8px",
                                          }}
                                        >
                                          {(tpl.mode || "—").toUpperCase()}
                                        </span>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }}
                    />
                  )}
                </div></div>

                {/* ── Pricing & Marketplace ── */}
                <div className="card mb-3"><div className="card-body">
                  <h5 className="mb-3">Pricing & Marketplace</h5>
                  <div className="row">
                    <div className="col-md-4 mb-3"><label className="form-label">Monthly Price ($)</label><input type="number" step="0.01" className="form-control" {...register("monthly_price")} /></div>
                    <div className="col-md-4 mb-3"><label className="form-label">Included Minutes</label><input type="number" step="1" className="form-control" {...register("included_minutes")} /></div>
                    <div className="col-md-4 mb-3 d-flex align-items-end">
                      <div className="form-check me-3">
                        <input className="form-check-input" type="checkbox" id="is_marketplace" {...register("is_marketplace")} />
                        <label htmlFor="is_marketplace" className="form-check-label">List on marketplace</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="is_active" {...register("is_active")} />
                        <label htmlFor="is_active" className="form-check-label">Active</label>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Marketplace description</label>
                      <textarea className="form-control" rows="3" placeholder="What customers see on the card" {...register("marketplace_description")} />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label d-block">
                        Profile picture
                        <small className="text-muted ms-2">Shown on the marketplace card (max 1 MB, image only)</small>
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        {(() => {
                          const pic = watch("profile_picture");
                          return pic ? (
                            <img
                              src={pic}
                              alt="Template"
                              style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid #e5e7eb" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 72, height: 72, borderRadius: 8,
                                background: "#f3f4f6", border: "1px dashed #cbd5e1",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: 11,
                              }}
                            >No image</div>
                          );
                        })()}
                        <div className="d-flex flex-column gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control form-control-sm"
                            style={{ maxWidth: 320 }}
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (!file) return;
                              if (!file.type.startsWith("image/")) {
                                triggerAlert && triggerAlert("error", "Invalid file", "Only image files are allowed.");
                                return;
                              }
                              if (file.size > 1024 * 1024) {
                                triggerAlert && triggerAlert("error", "Too large", "Image must be 1 MB or smaller.");
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = () => setValue("profile_picture", String(reader.result || ""), { shouldDirty: true });
                              reader.readAsDataURL(file);
                            }}
                          />
                          {watch("profile_picture") && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              style={{ alignSelf: "flex-start" }}
                              onClick={() => setValue("profile_picture", "", { shouldDirty: true })}
                            >
                              Remove image
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Features (bullet points on the card)</label>
                      {featureFields.map((f, idx) => (
                        <div key={f.id} className="d-flex gap-2 mb-2">
                          <input className="form-control" placeholder={`Feature #${idx + 1}`} {...register(`features.${idx}.text`)} />
                          {featureFields.length > 1 && (
                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeFeature(idx)}>−</button>
                          )}
                        </div>
                      ))}
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => appendFeature({ text: "" })}>+ Add Feature</button>
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Disclaimer</label>
                      <textarea className="form-control" rows="2" placeholder="Optional fine print" {...register("disclaimer")} />
                    </div>
                  </div>
                </div></div>

                {/* Actions */}
                <div className="d-flex justify-content-end gap-2 mb-5">
                  <button type="button" className="btn btn-light" disabled={saving} onClick={() => navigate("/v2/templates")}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : (isEdit ? "Save Changes" : "Publish Template")}</button>
                </div>
              </form>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
